import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useRealtimeInvalidate } from "@/hooks/realtime/useRealtimeInvalidate";
import { realtimeTopic } from "@/lib/realtime/channel";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { bindProposedContactTrack } from "@/lib/posthog/events";
import { capture } from "@/lib/sentry/capture";
import { logger } from "@/utils/logger";
import {
  createContact,
  deleteContact,
  fetchContacts,
  updateContact,
  type CreateContactInput,
} from "./repository";
import type { ContactDraft, ContactRow } from "./types";

/** Every contacts cache of one building — the Building view and each project. */
export const contactsKey = (buildingId: number) => ["contacts", buildingId] as const;

/**
 * The page's list. `projectId` null is the Building view (every project).
 * Waits for the org context: an org-less fetch would resolve RLS against the
 * wrong organisation.
 */
export function useContacts(buildingId: number | null, projectId: number | null) {
  const { isReady, currentOrganization } = useOrganizationContext();
  const enabled = buildingId != null && isReady && Boolean(currentOrganization?.id);
  return useQuery<ContactRow[]>({
    queryKey: [...contactsKey(buildingId ?? 0), projectId ?? "all"],
    enabled,
    queryFn: () => fetchContacts(buildingId as number, projectId),
    staleTime: 30_000,
  });
}

/**
 * Live updates for the whole building. `project_contacts.building_id` is
 * stamped from the project, so one filter covers both views. Mounted once, by
 * the page. Membership changes (a user gaining or losing project access) are
 * not on this channel; they arrive with the next refetch.
 */
export function useContactsRealtime(buildingId: number | null) {
  const { isReady, currentOrganization } = useOrganizationContext();
  useRealtimeInvalidate({
    enabled: buildingId != null && isReady && Boolean(currentOrganization?.id),
    topic: realtimeTopic("project-contacts", buildingId),
    bindings: [{ event: "*", table: "project_contacts", filter: `building_id=eq.${buildingId}` }],
    invalidate: [{ queryKey: contactsKey(buildingId ?? 0) }],
  });
}

export interface UpdateContactInput {
  contactId: number;
  draft: ContactDraft;
  isMember: boolean;
  expectedModifiedAt: string | null;
}

/** Writes settle when the server write settles; list refresh runs in the background. */
export function useContactMutations(buildingId: number) {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganizationContext();
  const bindContactTrack = () => bindProposedContactTrack(currentOrganization?.id);
  const refresh = () => {
    const reportRefreshError = (_error: unknown) => {
      const error = new Error("Contacts list refresh failed");
      const sentryEventId = capture(error, {
        tags: { feature: "contacts", action: "refresh" },
        context: { contact_write: { building_id: buildingId } },
        fingerprint: ["contacts", "refresh"],
      });
      void logger.error("ui", "contacts-refresh-failed", "Contacts list refresh failed", error, {
        building_id: buildingId,
        sentry_event_id: sentryEventId,
      });
    };
    try {
      void queryClient
        .invalidateQueries({ queryKey: contactsKey(buildingId) })
        .catch(reportRefreshError);
    } catch (error) {
      reportRefreshError(error);
    }
  };

  // A refused write usually means the list is stale (someone else got there
  // first, or the entry is no longer the viewer's): show the truth.
  const create = useMutation({
    mutationFn: (input: CreateContactInput) => createContact(input),
    onMutate: bindContactTrack,
    onSuccess: (_data, input, trackContact) => {
      refresh();
      trackContact({
        name: "contact_created",
        props: { scope: input.projectId == null ? "building" : "project" },
      });
    },
    onError: refresh,
  });
  const update = useMutation({
    mutationFn: ({ contactId, draft, isMember, expectedModifiedAt }: UpdateContactInput) =>
      updateContact(contactId, draft, isMember, expectedModifiedAt),
    onMutate: bindContactTrack,
    onSuccess: (_data, _input, trackContact) => {
      refresh();
      trackContact({ name: "contact_updated", props: {} });
    },
    onError: refresh,
  });
  const remove = useMutation({
    mutationFn: (contactId: number) => deleteContact(contactId),
    onMutate: bindContactTrack,
    onSuccess: (_data, _contactId, trackContact) => {
      refresh();
      trackContact({ name: "contact_deleted", props: {} });
    },
    onError: refresh,
  });

  return { create, update, remove };
}
